import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Hash, User, DollarSign, Check, Loader2, Zap, RotateCcw,
  Keyboard, Package, Lock, ChevronLeft
} from 'lucide-react';
import toast from 'react-hot-toast';
import ProductSearch from '../components/pos/ProductSearch';
import CustomerSelector from '../components/pos/CustomerSelector';
import PaymentPanel from '../components/pos/PaymentPanel';
import Modal from '../components/common/Modal';
import { usePosCart, resolveSellableProduct, productKey } from '../hooks/usePosCart';
import { usePosCheckout } from '../hooks/usePosCheckout';
import {
  getEffectiveUnits, getUnitPriceFromUnits, getAvailableStock, getMaxUnitsFor,
} from '../utils/formatStock';
import { fetchProducts } from '../redux/slices/productSlice';
import { fetchCustomers } from '../redux/slices/customerSlice';

const STEPS = [
  { id: 'search', label: 'Product', icon: Search },
  { id: 'qty', label: 'Quantity', icon: Hash },
  { id: 'customer', label: 'Customer', icon: User },
  { id: 'amount', label: 'Amount', icon: DollarSign },
  { id: 'checkout', label: 'Checkout', icon: Check },
];

const loadPosProducts = () => fetchProducts({ all: true, isActive: true });

export default function QuickCheckout() {
  const dispatch = useDispatch();
  const { products, loading: productLoading } = useSelector((state) => state.products);
  const { customers, loading: customerLoading } = useSelector((state) => state.customers);
  const { settings } = useSelector((state) => state.settings);
  const shiftStatus = useSelector((state) => state.shift.status);

  const taxRate = settings?.taxRate || 0;
  const dayClosed = shiftStatus !== 'OPEN';

  const [step, setStep] = useState('search');
  const [pending, setPending] = useState(null); // { product, unit, line }
  const [qtyInput, setQtyInput] = useState('1');
  const [amountInput, setAmountInput] = useState('');
  const [customer, setCustomer] = useState(null);
  const [unitChoice, setUnitChoice] = useState(null); // { product, units, index }
  const [showPayment, setShowPayment] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [showResetPrompt, setShowResetPrompt] = useState(false);
  const [focusKey, setFocusKey] = useState('search');

  const searchRef = useRef(null);
  const qtyRef = useRef(null);
  const amountRef = useRef(null);
  const checkoutRef = useRef(null);
  const customerRef = useRef(null);
  const unitRef = useRef(null);

  useEffect(() => {
    dispatch(loadPosProducts());
    dispatch(fetchCustomers({ limit: 200 }));
  }, [dispatch]);

  const openUnitPicker = useCallback((product) => {
    setPending(null);
    setUnitChoice({ product, units: getEffectiveUnits(product), index: 0 });
    setStep('unit');
    setFocusKey('unit');
  }, []);

  const {
    cartItems, setCartItems, cartPieces, subtotal, total,
    addWithUnit, updateQuantity, setLinePrice, clearCart, maxQuantityFor, findStockViolation,
  } = usePosCart({ products, dayClosed, taxRate, onUnitRequired: openUnitPicker });

  const { checkout, loading: checkoutLoading } = usePosCheckout({
    cartItems,
    cartPieces,
    subtotal,
    discount: '',
    taxRate,
    customer,
    notes: '',
    products,
    findStockViolation,
  });

  const line = cartItems[0] || null;
  const currentProduct = line
    ? products.find((p) => String(productKey(p)) === String(productKey(line))) || line
    : null;
  // 'unit' is a sub-step of product selection, so the bar stays on step 1.
  const stepIndex = step === 'unit' ? 0 : Math.max(0, STEPS.findIndex((s) => s.id === step));

  /* ── focus plumbing: one place decides which field owns the keyboard ────── */
  useEffect(() => {
    const targets = {
      search: searchRef,
      qty: qtyRef,
      amount: amountRef,
      checkout: checkoutRef,
      customer: customerRef,
      unit: unitRef,
    };
    const target = targets[focusKey];
    if (!target?.current) return;
    const id = window.setTimeout(() => {
      target.current?.focus();
      if (target.current?.select) target.current.select();
    }, 40);
    return () => window.clearTimeout(id);
  }, [focusKey, step]);

  const reset = useCallback((message) => {
    setStep('search');
    setPending(null);
    setUnitChoice(null);
    setQtyInput('1');
    setAmountInput('');
    setCustomer(null);
    clearCart();
    if (message) toast.success(message);
    setFocusKey('search');
  }, [clearCart]);

  /* ── Step 1: search ────────────────────────────────────────────────────── */
  const pickProduct = useCallback((raw) => {
    const product = resolveSellableProduct(raw, products);
    if (!product) {
      toast.error(`No product found for "${raw?.productName || raw?.name || ''}"`);
      setFocusKey('search');
      return;
    }
    if (getAvailableStock(product) <= 0) {
      toast.error(`${product.productName || product.name} is out of stock`);
      setFocusKey('search');
      return;
    }
    const units = getEffectiveUnits(product);
    const sellable = units.filter((u) => u.sellable);
    if (sellable.length > 1) {
      openUnitPicker(product);
      return;
    }
    setPending({ product, unit: units[0]?.name || 'Piece' });
    setQtyInput('1');
    setStep('qty');
    setFocusKey('qty');
  }, [products, openUnitPicker]);

  const pickBarcode = useCallback((code) => {
    const needle = String(code || '').trim().toLowerCase();
    const match = products.find((p) => p.active !== false && p.isActive !== false && (
      String(p.barcode || '').toLowerCase() === needle
      || String(p.serialNumber || '').toLowerCase() === needle
    ));
    if (!match) {
      toast.error(`No product found with barcode: ${code}`);
      setFocusKey('search');
      return;
    }
    pickProduct(match);
  }, [products, pickProduct]);

  /* ── unit picker (multi-unit products only) ────────────────────────────── */
  const confirmUnit = useCallback(() => {
    if (!unitChoice) return;
    const unit = unitChoice.units[unitChoice.index];
    if (!unit) return;
    if (getMaxUnitsFor(unitChoice.product, unit.name) <= 0) {
      toast.error(`${unitChoice.product.productName} has no ${unit.name} left in stock`);
      setStep('search');
      setUnitChoice(null);
      setFocusKey('search');
      return;
    }
    setPending({ product: unitChoice.product, unit: unit.name });
    setQtyInput('1');
    setUnitChoice(null);
    setStep('qty');
    setFocusKey('qty');
  }, [unitChoice]);

  /* ── Step 2: quantity (shared stock cap) ───────────────────────────────── */
  const confirmQty = useCallback(() => {
    if (!pending) return;
    const qty = parseInt(qtyInput, 10);
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error('Enter a valid quantity');
      setFocusKey('qty');
      return;
    }

    // Editing an existing line: the cart hook caps it against live stock.
    if (pending.editing) {
      updateQuantity(pending.editing, qty);
      setPending(null);
      setStep('customer');
      setFocusKey('customer');
      return;
    }

    const probe = { ...pending.product, quantity: qty, saleUnit: pending.unit, piecesDeducted: 0 };
    const { available, maxQuantity } = maxQuantityFor(probe);
    if (maxQuantity <= 0) {
      toast.error(`${pending.product.productName} is out of stock`);
      setStep('search');
      setPending(null);
      setFocusKey('search');
      return;
    }
    if (qty > maxQuantity) {
      toast.error(`Only ${available} in stock – maximum ${maxQuantity} ${pending.unit}`);
      setQtyInput(String(maxQuantity));
      setFocusKey('qty');
      return;
    }

    const price = getUnitPriceFromUnits(pending.product, pending.unit);
    const added = addWithUnit(pending.product, { unit: pending.unit, quantity: qty, price });
    if (!added) {
      setFocusKey('qty');
      return;
    }
    setStep('customer');
    setFocusKey('customer');
  }, [pending, qtyInput, maxQuantityFor, addWithUnit, updateQuantity]);

  /* ── Step 3: customer (optional) ───────────────────────────────────────── */
  const confirmCustomer = useCallback((c) => {
    setCustomer(c);
    setAmountInput('');
    setStep('amount');
    setFocusKey('amount');
  }, []);

  const skipCustomer = useCallback(() => {
    setCustomer(null);
    setAmountInput('');
    setStep('amount');
    setFocusKey('amount');
  }, []);

  /* ── Step 4: custom amount (the POS line-price mechanism) ──────────────── */
  const confirmAmount = useCallback(() => {
    if (!line) {
      toast.error('Add a product first');
      setStep('search');
      setFocusKey('search');
      return;
    }
    const raw = String(amountInput).trim();
    if (raw !== '') {
      const value = Number(raw);
      if (!Number.isFinite(value) || value < 0) {
        toast.error('Enter a valid amount');
        setFocusKey('amount');
        return;
      }
      setLinePrice(line, value);
    } else {
      // Empty = use the product's own actual selling price.
      setLinePrice(line, null);
    }
    setStep('checkout');
    setFocusKey('checkout');
  }, [amountInput, line, setLinePrice]);

  /* ── Step 5: checkout (existing sale creation path) ────────────────────── */
  const completeSale = useCallback(async (paymentData) => {
    const outcome = await checkout(paymentData);
    if (!outcome.ok) {
      if (outcome.reason === 'insufficient-stock' || outcome.reason === 'error') {
        setFocusKey('search');
      }
      return false;
    }
    setLastSale({
      total: outcome.sale?.totalAmount ?? total,
      transactionId: outcome.sale?.transactionId,
      items: cartItems.length,
    });
    setShowPayment(false);
    reset();
    dispatch(loadPosProducts());
    return true;
  }, [checkout, total, cartItems.length, reset, dispatch]);

  const quickCash = useCallback(() => completeSale({
    method: 'cash',
    totalAmount: total,
    amountTendered: total,
    change: 0,
    shortfall: 0,
  }), [completeSale, total]);

  /* ── global shortcuts ──────────────────────────────────────────────────── */
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'F1') {
        e.preventDefault();
        setStep('search');
        setFocusKey('search');
      } else if (e.key === 'F2') {
        e.preventDefault();
        if (cartItems.length > 0) setShowPayment(true);
      } else if (e.key === 'F3') {
        e.preventDefault();
        setShowResetPrompt(true);
      } else if (step === 'unit' && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        // Walk the sellable units without leaving the keyboard.
        e.preventDefault();
        setUnitChoice((c) => {
          if (!c) return c;
          const sellableIdx = [];
          c.units.forEach((u, i) => {
            if (u.sellable) sellableIdx.push(i);
          });
          if (sellableIdx.length === 0) return c;
          const at = sellableIdx.indexOf(c.index);
          const stepBy = e.key === 'ArrowRight' ? 1 : -1;
          const nextAt = (at + stepBy + sellableIdx.length) % sellableIdx.length;
          return { ...c, index: sellableIdx[nextAt] };
        });
      } else if (e.key === 'Escape') {
        if (showPayment) setShowPayment(false);
        else if (unitChoice) {
          setUnitChoice(null);
          setStep('search');
          setFocusKey('search');
        } else if (step !== 'search' || cartItems.length > 0) {
          reset();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cartItems.length, reset, showPayment, step, unitChoice]);

  const unitName = line?.saleUnit || pending?.unit || 'Piece';
  const displayPrice = line ? (line.salePrice ?? line.price ?? 0) : 0;
  const listPrice = currentProduct ? getUnitPriceFromUnits(currentProduct, unitName) : 0;
  const isCustomPrice = !!line && displayPrice !== listPrice;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="p-4 md:p-6"
    >
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-primary-600 flex items-center justify-center">
              <Zap size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Quick Checkout</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Keyboard-only till — same pricing, stock rules and order flow as POS
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300">
              <Keyboard size={13} />
              Enter advances · F1 search · F2 payment · F3 reset · Esc cancel
            </span>
            {dayClosed && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400">
                <Lock size={13} /> Shop closed
              </span>
            )}
          </div>
        </div>

        <StepBar current={stepIndex} />

        <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-4 items-start">
          {/* ── the flow ─────────────────────────────────────────────────── */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
            {dayClosed && (
              <p className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                <Lock size={14} /> The business day is closed. Start a new day from POS to sell again.
              </p>
            )}

            {/* Step 1 — search */}
            <Field step={1} label="Product" active={step === 'search'} done={stepIndex > 0} hint="Type name, SKU or barcode, then Enter">
              <ProductSearch
                products={products}
                onAddToCart={pickProduct}
                onBarcodeSearch={pickBarcode}
                inputRef={searchRef}
                autoRefocus={false}
                emptyResultsLabel="No product found — press Enter to search by barcode"
              />
            </Field>

            {/* Unit picker — only for multi-unit products */}
            <AnimatePresence>
              {unitChoice && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <Field step="1b" label="Sale Unit" active hint="← → to change, Enter to confirm">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {unitChoice.units.filter((u) => u.sellable).map((u) => {
                        const idx = unitChoice.units.indexOf(u);
                        const max = getMaxUnitsFor(unitChoice.product, u.name);
                        const selected = unitChoice.index === idx;
                        return (
                          <button
                            key={u.name}
                            type="button"
                            onClick={() => setUnitChoice((c) => ({ ...c, index: idx }))}
                            disabled={max <= 0}
                            className={`px-3 py-2.5 rounded-lg border text-left transition-all disabled:opacity-40 ${
                              selected
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
                            }`}
                          >
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{u.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Rs. {(u.salePrice || 0).toLocaleString()} · {max > 0 ? `${max} left` : 'none left'}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                    <button
                      ref={unitRef}
                      type="button"
                      onClick={confirmUnit}
                      className="mt-3 w-full py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                    >
                      Continue
                    </button>
                  </Field>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Step 2 — quantity */}
            <Field
              step={2}
              label="Quantity"
              active={step === 'qty'}
              done={stepIndex > 1}
              hint={pending ? `Max ${quantityCap(pending, maxQuantityFor)} ${pending.unit} in stock` : 'Enter to confirm'}
            >
              <div className="flex items-center gap-2">
                <input
                  ref={qtyRef}
                  type="number"
                  min="1"
                  value={qtyInput}
                  onChange={(e) => setQtyInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      confirmQty();
                    }
                  }}
                  placeholder="1"
                  className="flex-1 px-4 py-3 text-lg font-semibold bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:border-primary-500"
                />
                <button
                  type="button"
                  onClick={confirmQty}
                  className="px-4 py-3 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Enter
                </button>
              </div>
            </Field>

            {/* Step 3 — customer (optional) */}
            <Field
              step={3}
              label="Customer (optional)"
              active={step === 'customer'}
              done={stepIndex > 2}
              hint="Enter to select · type to search · Esc to skip"
            >
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <CustomerSelector
                  customers={customers}
                  selectedCustomer={customer}
                  onSelect={confirmCustomer}
                  onClear={() => setCustomer(null)}
                  onSkip={skipCustomer}
                  loading={customerLoading}
                  enableKeyboard
                  inputRef={customerRef}
                  variant="full"
                />
              </div>
            </Field>

            {/* Step 4 — custom amount (optional) */}
            <Field
              step={4}
              label="Custom Amount (optional)"
              active={step === 'amount'}
              done={stepIndex > 3}
              hint="Enter to use actual price"
            >
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    ref={amountRef}
                    type="number"
                    min="0"
                    value={amountInput}
                    onChange={(e) => setAmountInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        confirmAmount();
                      }
                    }}
                    placeholder={line ? String(line.salePrice ?? line.price ?? 0) : 'Actual price'}
                    className="w-full pl-9 pr-3 py-3 text-lg font-semibold bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:border-primary-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={confirmAmount}
                  className="px-4 py-3 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Enter
                </button>
              </div>
            </Field>

            {/* Step 5 — checkout */}
            <Field step={5} label="Checkout" active={step === 'checkout'} hint="Enter to complete">
              <div className="space-y-2">
                <button
                  ref={checkoutRef}
                  type="button"
                  onClick={quickCash}
                  disabled={!line || checkoutLoading || dayClosed}
                  className="w-full py-3.5 text-base font-semibold rounded-xl bg-primary-600 hover:bg-primary-700 text-white shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {checkoutLoading ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                  Complete Sale — Rs. {total.toLocaleString()}
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setShowPayment(true)}
                    disabled={!line}
                    className="py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    Payment method (F2)
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowResetPrompt(true)}
                    className="py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center gap-1.5"
                  >
                    <RotateCcw size={14} /> Reset (F3)
                  </button>
                </div>
              </div>
            </Field>
          </div>

          {/* ── live summary ─────────────────────────────────────────────── */}
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Current Sale</h2>

              {!line ? (
                <div className="py-8 text-center">
                  <Package size={28} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">No product added yet</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Start typing a product name</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {line.productName || line.name}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {line.quantity} {unitName}
                        {currentProduct ? ` · ${getAvailableStock(currentProduct)} in stock` : ''}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                      Rs. {(displayPrice * (line.quantity || 1)).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500 dark:text-gray-400">Unit price</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      Rs. {displayPrice.toLocaleString()}
                      {isCustomPrice && (
                        <span className="ml-1.5 text-[10px] text-amber-500">custom</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500 dark:text-gray-400">Customer</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {customer?.fullName || customer?.name || 'Walk-in'}
                    </span>
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total</span>
                    <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                      Rs. {total.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (!line) return;
                        setPending({ product: currentProduct, unit: line.saleUnit, editing: line });
                        setQtyInput(String(line.quantity || 1));
                        setStep('qty');
                        setFocusKey('qty');
                      }}
                      className="flex-1 py-2 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      Change qty
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCartItems((prev) => prev.filter((i) => productKey(i) !== productKey(line)));
                        setStep('search');
                        setFocusKey('search');
                      }}
                      className="flex-1 py-2 text-xs font-medium rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </div>

            <AnimatePresence>
              {lastSale && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex items-center gap-2.5 p-3.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800"
                >
                  <Check size={16} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  <p className="text-sm text-emerald-700 dark:text-emerald-300">
                    {lastSale.transactionId ? `${lastSale.transactionId} · ` : ''}
                    Rs. {Number(lastSale.total || 0).toLocaleString()} saved
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <Modal isOpen={showPayment} onClose={() => setShowPayment(false)} size="md" title="Payment">
        <PaymentPanel
          totalAmount={total}
          onConfirm={(paymentData) => completeSale(paymentData)}
          onCancel={() => setShowPayment(false)}
          loading={checkoutLoading}
          customer={customer}
          customers={customers}
          customerLoading={customerLoading}
          onCustomerSelect={setCustomer}
          onCustomerClear={() => setCustomer(null)}
          confirmOnEnter
        />
      </Modal>

      <Modal isOpen={showResetPrompt} onClose={() => setShowResetPrompt(false)} size="sm" title="Reset Sale">
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Clear the current sale and start over? Stock is not affected.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowResetPrompt(false)}
              className="flex-1 py-2.5 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Keep
            </button>
            <button
              type="button"
              onClick={() => {
                setShowResetPrompt(false);
                reset();
              }}
              className="flex-1 py-2.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600"
            >
              Reset
            </button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}

/* ── small presentational helpers ─────────────────────────────────────────── */

function StepBar({ current }) {
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const state = i < current ? 'done' : i === current ? 'active' : 'todo';
        return (
          <React.Fragment key={s.id}>
            <div
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                state === 'active'
                  ? 'bg-primary-600 text-white'
                  : state === 'done'
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                    : 'bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700'
              }`}
            >
              <Icon size={12} />
              {i + 1}. {s.label}
            </div>
            {i < STEPS.length - 1 && <ChevronLeft size={12} className="text-gray-300 dark:text-gray-600 rotate-180" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Field({ step, label, active, done, hint, children }) {
  return (
    <div className={`rounded-xl border p-4 transition-colors ${
      active
        ? 'border-primary-400 dark:border-primary-600 bg-primary-50/40 dark:bg-primary-900/10'
        : 'border-gray-200 dark:border-gray-700'
    }`}>
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2">
          <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
            active
              ? 'bg-primary-600 text-white'
              : done
                ? 'bg-emerald-500 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
          }`}
          >
            {done ? <Check size={11} /> : step}
          </span>
          <p className={`text-sm font-medium ${active ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
            {label}
          </p>
        </div>
        {hint && <p className="text-[11px] text-gray-400 dark:text-gray-500 text-right">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

/** Highest quantity the cart hook will allow for this product/unit. */
function quantityCap(pending, maxQuantityFor) {
  if (!pending) return 0;
  // When editing, the line's own pieces are already reserved.
  const probe = pending.editing
    ? pending.editing
    : { ...pending.product, quantity: 1, saleUnit: pending.unit, piecesDeducted: 0 };
  return maxQuantityFor(probe).maxQuantity;
}
